import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Container,
  Grid,
  Box,
  styled,
  TextField,
  InputAdornment,
  Chip,
  Skeleton,
  Button
} from "@mui/material";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import allArticles from "../../data/caseStudies.json";
import useSEO from "../../hooks/useSEO";
import bgImage from "../../components/UseCases/assets/UseCasesBgImage.svg";
import StarIcon from "@mui/icons-material/Star";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const FEATURED_CASE_STUDY_ID = "successful-deployment-of-ai-voice-agents-for-understanding-consumer-adoption-of-electric-vehicles";

const StyledLink = styled(Link)(({ theme }) => ({
  color: "inherit",
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const FeaturedContainer = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)",
  borderRadius: 24,
  padding: "40px 32px",
  marginBottom: "48px",
  position: "relative",
  overflow: "hidden",
  border: "1px solid rgba(79, 70, 229, 0.2)",
  backdropFilter: "blur(10px)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%)",
    pointerEvents: "none",
  },
}));

const FeaturedBadge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "#4f46e5",
  color: "white",
  padding: "8px 16px",
  borderRadius: "20px",
  fontSize: "14px",
  fontWeight: 600,
  marginBottom: "24px",
  boxShadow: "0 4px 12px rgba(79, 70, 229, 0.3)",
}));

const FeaturedImage = styled(CardMedia)(({ theme }) => ({
  borderRadius: 16,
  overflow: "hidden",
  height: "300px",
  boxShadow: "0 12px 24px rgba(0, 0, 0, 0.15)",
  transition: "transform 0.5s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
  },
}));

const FeaturedTitle = styled(Typography)(({ theme }) => ({
  fontSize: "32px",
  fontWeight: 800,
  lineHeight: 1.2,
  marginBottom: "16px",
  color: "white",
  textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  [theme.breakpoints.down('sm')]: {
    fontSize: "24px",
  },
}));

const FeaturedDescription = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  lineHeight: 1.6,
  color: "rgba(255, 255, 255, 0.9)",
  marginBottom: "24px",
  [theme.breakpoints.down('sm')]: {
    fontSize: "16px",
  },
}));

const FeaturedMeta = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "24px",
  marginBottom: "24px",
  flexWrap: "wrap",
}));

const MetaItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "rgba(255, 255, 255, 0.8)",
  fontSize: "14px",
}));

const FeaturedButton = styled(Button)(({ theme }) => ({
  backgroundColor: "white",
  color: "#4f46e5",
  padding: "12px 24px",
  borderRadius: "12px",
  fontWeight: 600,
  fontSize: "16px",
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#f8fafc",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const FeaturedCategoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  color: "white",
  fontWeight: 500,
  fontSize: "12px",
  height: "28px",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
}));

// Updated with dark theme
const PageNumber = styled(Box)(({ theme, active }) => ({
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  cursor: "pointer",
  background: active
    ? "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)"
    : "rgba(255,255,255,0.05)",
  border: active ? "none" : "1px solid rgba(255,255,255,0.1)",
  color: "#fff",
  fontWeight: active ? 600 : 400,
  transition: "all 0.3s ease",
  "&:hover": {
    background: active
      ? "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)"
      : "rgba(255,255,255,0.1)",
    transform: "translateY(-2px)",
  },
}));

const PageNavigation = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  cursor: "pointer",
  backgroundColor: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "rgba(255,255,255,0.7)",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#fff",
  },
}));

// Glassmorphism card styling
const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 16,
  overflow: "hidden",
  background: "rgba(255,255,255,0.03)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.08)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-8px)",
    borderColor: "rgba(226, 104, 74, 0.3)",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3), 0 0 30px rgba(226, 104, 74, 0.1)",
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 200,
  transition: "transform 0.5s",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

// Updated category chip with gradient background
const CategoryChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: 12,
  left: 12,
  background: "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)",
  color: "white",
  fontWeight: 600,
  fontSize: "0.7rem",
  height: "26px",
  border: "none",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
}));

// Dark theme search box with glow
const SearchBox = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 16,
    backgroundColor: "rgba(255,255,255,0.05)",
    backdropFilter: "blur(10px)",
    color: "#fff",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.1)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.2)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#E2684A",
      boxShadow: "0 0 20px rgba(226, 104, 74, 0.2)",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    "&::placeholder": {
      color: "rgba(255,255,255,0.5)",
      opacity: 1,
    },
  },
}));

const CategoryButton = memo(
  ({ category, activeCategory, handleCategoryClick }) => (
    <Box
      onClick={() => handleCategoryClick(category)}
      sx={{
        px: 3,
        py: 1,
        borderRadius: "14px",
        background:
          activeCategory === category
            ? "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)"
            : "rgba(255,255,255,0.05)",
        border:
          activeCategory === category
            ? "none"
            : "1px solid rgba(255,255,255,0.1)",
        color: "#fff",
        cursor: "pointer",
        fontWeight: 500,
        fontSize: "0.9rem",
        transition: "all 0.3s ease",
        "&:hover": {
          background:
            activeCategory === category
              ? "linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)"
              : "rgba(255,255,255,0.1)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {category}
    </Box>
  )
);

const FeaturedCaseStudy = memo(({ article }) => {
  const getPrimaryCategory = useCallback((article) => {
    if (!article.categories || article.categories.length === 0) {
      return null;
    }
    return (
      article.categories.find((cat) => cat !== "All") || article.categories[0]
    );
  }, []);

  if (!article) return null;

  return (
    <FeaturedContainer>
      <FeaturedBadge>
        <StarIcon sx={{ fontSize: 16 }} />
        Featured Case Study
      </FeaturedBadge>

      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
          <Box>
            {getPrimaryCategory(article) && (
              <FeaturedCategoryChip
                label={getPrimaryCategory(article)}
                size="small"
                sx={{ mb: 2 }}
              />
            )}

            <FeaturedTitle variant="h2">
              {article.title}
            </FeaturedTitle>

            <FeaturedDescription>
              {article.description || article.excerpt ||
                "Discover how our AI solutions transformed this business and delivered exceptional results."}
            </FeaturedDescription>

            <FeaturedMeta>
              <MetaItem>
                <AccessTimeIcon sx={{ fontSize: 16 }} />
                {article.readTime}
              </MetaItem>
              <MetaItem>
                {article.date}
              </MetaItem>
            </FeaturedMeta>

            <StyledLink to={`/case-studies/${article.id}`}>
              <FeaturedButton
                endIcon={<ArrowForwardIcon />}
                size="large"
              >
                Read Full Case Study
              </FeaturedButton>
            </StyledLink>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledLink to={`/case-studies/${article.id}`}>
            <FeaturedImage
              component="img"
              image={article.image}
              alt={article.title}
              loading="lazy"
            />
          </StyledLink>
        </Grid>
      </Grid>
    </FeaturedContainer>
  );
});


const BlogCard = memo(({ article }) => {
  const getPrimaryCategory = useCallback((article) => {
    if (!article.categories || article.categories.length === 0) {
      return null;
    }
    return (
      article.categories.find((cat) => cat !== "All") || article.categories[0]
    );
  }, []);

  return (
    <StyledLink to={`/case-studies/${article.id}`}>
      <StyledCard>
        <Box sx={{ position: "relative" }}>
          <StyledCardMedia
            component="img"
            image={article.image}
            alt={article.title}
            loading="lazy"
          />
          {getPrimaryCategory(article) && (
            <CategoryChip label={getPrimaryCategory(article)} size="small" />
          )}
        </Box>
        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "transparent",
            p: 3,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: "1.1rem",
              lineHeight: 1.4,
              color: "#fff",
              mb: 2,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {article.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: "auto",
              color: "rgba(255,255,255,0.5)",
              fontSize: "0.85rem",
            }}
          >
            {article.date} • {article.readTime}
          </Typography>
        </CardContent>
      </StyledCard>
    </StyledLink>
  );
});

const NoResults = memo(() => (
  <Box sx={{ textAlign: "center", my: 8 }}>
    <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.7)", mb: 2 }}>
      No articles found matching your search.
    </Typography>
    <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.5)" }}>
      Try different keywords or browse all our articles.
    </Typography>
  </Box>
));

const CaseStudiesPaginationComponent = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedArticles, setDisplayedArticles] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const ITEMS_PER_PAGE = 9;

  const categories = useMemo(
    () => ["All", "AI Agents", "Marketing & Sales", "Voice", "OCR"],
    []
  );

  const featuredCaseStudy = useMemo(() => {
    return allArticles.find(article => article.id === FEATURED_CASE_STUDY_ID) || allArticles[0];
  }, []);

  // Filter out featured case study from regular articles
  const nonFeaturedArticles = useMemo(() => {
    return allArticles.filter(article => article.id !== featuredCaseStudy?.id);
  }, [featuredCaseStudy]);

  useSEO({
    title: `${activeCategory !== "All" ? activeCategory + " - " : ""
      }Case Studies - Beanbag AI`, // Fixed: Changed from "Blog" to "Case Studies"
    description:
      "Explore our collection of case studies showing how AI solutions transformed businesses and delivered exceptional results.",
    canonical: "https://www.beanbag.ai/case-studies", // Fixed: Updated URL
    keywords:
      "AI case studies, machine learning success stories, business transformation, AI implementation, " +
      categories.join(", "),

    // Open Graph tags
    ogTitle: `${activeCategory !== "All" ? activeCategory + " - " : ""
      }Beanbag AI Case Studies`,
    ogDescription:
      "Discover real-world examples of how our AI solutions transformed businesses and delivered exceptional results.",
    ogImage: "https://www.beanbag.ai/images/case-studies-social-card.jpg",
    ogUrl: "https://www.beanbag.ai/case-studies",
    ogType: "website", // Changed from "blog" to "website"

    // Twitter Card tags
    twitterCard: "summary_large_image",
    twitterSite: "@BeanbagAI",

    // JSON-LD structured data
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage", // More appropriate than "Blog"
      headline: "Beanbag AI Case Studies",
      description:
        "Explore our collection of case studies showing how AI solutions transformed businesses and delivered exceptional results.",
      url: "https://www.beanbag.ai/case-studies",
      publisher: {
        "@type": "Organization",
        name: "Beanbag AI",
        logo: {
          "@type": "ImageObject",
          url: "https://www.beanbag.ai/logo192.png",
        },
      },
    },
  });

  const handleCategoryClick = useCallback((category) => {
    setActiveCategory(category);
    setSearchTerm("");

    const filtered =
      category === "All"
        ? allArticles
        : allArticles.filter(
          (article) =>
            article.categories && article.categories.includes(category)
        );

    setFilteredArticles(filtered);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    const imageLoader = new Image();
    imageLoader.onload = () => setBackgroundLoaded(true);
    imageLoader.src = bgImage;

    return () => clearTimeout(timerId);
  }, []);

  useEffect(() => {
    const debouncedSearch = setTimeout(() => {
      const filtered = searchTerm
        ? allArticles.filter(
          (article) =>
            article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (article.content &&
              article.content
                .toLowerCase()
                .includes(searchTerm.toLowerCase()))
        )
        : activeCategory === "All"
          ? allArticles
          : allArticles.filter(
            (article) =>
              article.categories && article.categories.includes(activeCategory)
          );

      setFilteredArticles(filtered);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(debouncedSearch);
  }, [searchTerm, activeCategory]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setDisplayedArticles(filteredArticles.slice(startIndex, endIndex));

    if (currentPage > 1) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [currentPage, filteredArticles]);

  const totalPages = useMemo(
    () => Math.ceil(filteredArticles.length / ITEMS_PER_PAGE),
    [filteredArticles.length, ITEMS_PER_PAGE]
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const renderPagination = useCallback(() => {
    const pages = [];

    if (currentPage > 1) {
      pages.push(
        <PageNavigation
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          sx={{ mr: 1 }}
        >
          ‹
        </PageNavigation>
      );
    }

    for (let i = 1; i <= Math.min(5, totalPages); i++) {
      pages.push(
        <PageNumber
          key={i}
          active={currentPage === i ? 1 : 0}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </PageNumber>
      );
    }

    if (totalPages > 6) {
      pages.push(
        <Box key="ellipsis" sx={{ mx: 1 }}>
          ...
        </Box>
      );
    }

    if (totalPages > 5) {
      pages.push(
        <PageNumber
          key={totalPages}
          active={currentPage === totalPages ? 1 : 0}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </PageNumber>
      );
    }

    if (currentPage < totalPages) {
      pages.push(
        <PageNavigation
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          sx={{ ml: 1 }}
        >
          ›
        </PageNavigation>
      );
    }

    return pages;
  }, [currentPage, totalPages, handlePageChange]);

  return (
    <Container maxWidth="lg" sx={{ py: 4, marginTop: "100px" }}>
      <Box sx={{ textAlign: "center", mb: 6 }}>
        {isLoading ? (
          <>
            <Skeleton
              variant="text"
              height={80}
              width="60%"
              sx={{ mx: "auto", mb: 2 }}
            />
            <Skeleton
              variant="text"
              height={30}
              width="80%"
              sx={{ mx: "auto", mb: 1 }}
            />
            <Skeleton
              variant="text"
              height={30}
              width="70%"
              sx={{ mx: "auto", mb: 4 }}
            />
          </>
        ) : (
          <>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                color: "white",
                mb: 2,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
              }}
            >
              {activeCategory !== "All"
                ? `${activeCategory} Articles`
                : "All Case Studies"}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "white",
                maxWidth: "800px",
                mx: "auto",
                mb: 4,
                fontSize: { xs: "1rem", md: "1.25rem" },
                lineHeight: 1.6,
              }}
            >
              Explore our collection of articles, tutorials, and insights about
              AI, machine learning, and how our platform can help transform your
              business.
            </Typography>
          </>
        )}

        {!isLoading && featuredCaseStudy && (
          <FeaturedCaseStudy article={featuredCaseStudy} />
        )}

        <SearchBox
          fullWidth
          placeholder="Search articles..."
          variant="outlined"
          onChange={handleSearch}
          value={searchTerm}
          sx={{ maxWidth: "600px", mx: "auto", mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 4,
          }}
        >
          {categories.map((category) => (
            <CategoryButton
              key={category}
              category={category}
              activeCategory={activeCategory}
              handleCategoryClick={handleCategoryClick}
            />
          ))}
        </Box>
      </Box>

      {isLoading ? (
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ height: "100%" }}>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" height={30} width="80%" />
                  <Skeleton variant="text" height={20} width="40%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : displayedArticles.length > 0 ? (
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {displayedArticles.map((article) => (
            <Grid item xs={12} sm={6} md={4} key={article.id}>
              <BlogCard article={article} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <NoResults />
      )}

      {filteredArticles.length > ITEMS_PER_PAGE && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            mt: 4,
          }}
        >
          {renderPagination()}
        </Box>
      )}
    </Container>
  );
};

export default CaseStudiesPaginationComponent;
